// Winter duty roster helpers (Jill 2026-09-11). The winter sheet lists 32
// weekly periods across the top and one row per pilot; an X means the pilot
// is ON DISPATCH that week (the opposite of the summer sheet, where X is off).
// Pure functions so the parse and the on-dispatch lookups are unit-testable.
//
// Data shapes:
//   period  { period, start, end, needed }              ISO dates, inclusive
//   duty    { pilot_id, period, on_duty, source }        source 'sheet' | 'trade'

const iso = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
export const addDays = (s, n) => { const d = fromIso(s); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const daysBetween = (a, b) => Math.round((fromIso(b) - fromIso(a)) / 86400000);

// A sheet date cell can be a real Date, an ISO string, or "16-Sep" text.
export function cellToIso(v, fallbackYear) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return iso(v);
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[-/ ]([A-Za-z]{3})/);
  if (m && fallbackYear) {
    const mon = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"].indexOf(m[2].toLowerCase());
    if (mon >= 0) return `${fallbackYear}-${String(mon + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  return null;
}

const str = (v) => (v == null ? "" : String(v.richText ? v.richText.map((t) => t.text).join("") : (v.result !== undefined ? v.result : v))).trim();

// grid: rows of cell values (row-major, 0-based), as read from the workbook.
// Returns { periods, pilots: [{ label, periods: [n, ...] }], problems: [text] }.
// Header slips get corrected, and every correction is named in `problems` so
// the person publishing sees what was changed rather than trusting it blind.
export function parseWinterGrid(grid) {
  const problems = [];
  const rows = (grid || []).map((r) => (r || []).map(str));
  const startRow = rows.findIndex((r) => r.some((c) => /^START\b/i.test(c)));
  const endRow = rows.findIndex((r, i) => i > startRow && r.some((c) => /^END\b/i.test(c)));
  if (startRow < 0 || endRow < 0) return { periods: [], pilots: [], problems: ["Could not find the START and END rows on the sheet."] };
  const nameCol = rows[startRow].findIndex((c) => /^START\b/i.test(c));
  // the period-number row: the nearest row above START whose cells past the
  // name column read 1, 2, 3 ...
  let numRow = -1;
  for (let i = startRow - 1; i >= 0; i--) {
    const cells = rows[i].slice(nameCol + 1).filter(Boolean);
    if (cells.length >= 3 && cells.slice(0, 3).join(",") === "1,2,3") { numRow = i; break; }
  }
  if (numRow < 0) return { periods: [], pilots: [], problems: ["Could not find the period numbers row (1, 2, 3 ...)."] };
  const cols = [];
  rows[numRow].forEach((c, ci) => { if (ci > nameCol && /^\d+$/.test(c)) cols.push({ col: ci, period: Number(c) }); });

  // raw dates; the END row carries real years, so its majority year anchors
  // any "16-Sep" style text
  const rawGrid = grid || [];
  const endYears = cols.map(({ col }) => cellToIso(rawGrid[endRow]?.[col])).filter(Boolean).map((d) => Number(d.slice(0, 4)));
  const anchorYear = endYears.length ? endYears.sort()[Math.floor(endYears.length / 2)] : new Date().getUTCFullYear();
  const periods = cols.map(({ col, period }) => ({
    period,
    start: cellToIso(rawGrid[startRow]?.[col], anchorYear),
    end: cellToIso(rawGrid[endRow]?.[col], anchorYear),
    needed: null,
  }));
  // Fix header slips: a period's start must be the day after the previous
  // period's end, and each period must run forward. (The 26-27 sheet typed
  // week 1's start as 2024 and week 32's end as 2026.)
  for (let i = 0; i < periods.length; i++) {
    const p = periods[i];
    if (!p.start || !p.end) { problems.push(`Period ${p.period}: missing a start or end date.`); continue; }
    if (i > 0 && periods[i - 1].end) {
      const want = addDays(periods[i - 1].end, 1);
      if (p.start !== want) { problems.push(`Period ${p.period}: start ${p.start} corrected to ${want} (the day after period ${periods[i - 1].period} ends).`); p.start = want; }
    } else if (daysBetween(p.start, p.end) > 60 || p.end < p.start) {
      // first period: put the start in the same season as its end
      const fixed = `${p.end.slice(0, 4)}-${p.start.slice(5)}`;
      const corrected = fixed > p.end ? `${Number(p.end.slice(0, 4)) - 1}-${p.start.slice(5)}` : fixed;
      problems.push(`Period ${p.period}: start ${p.start} corrected to ${corrected}.`);
      p.start = corrected;
    }
    if (p.end < p.start) {
      const fixed = `${p.start.slice(0, 4)}-${p.end.slice(5)}`;
      const corrected = fixed < p.start ? `${Number(p.start.slice(0, 4)) + 1}-${p.end.slice(5)}` : fixed;
      problems.push(`Period ${p.period}: end ${p.end} corrected to ${corrected}.`);
      p.end = corrected;
    }
  }

  // pilot rows: below END until the footer ("Pilots Needed" / "Period Selected")
  const pilots = [];
  for (let i = endRow + 1; i < rows.length; i++) {
    const r = rows[i];
    const label = r[nameCol] || "";
    if (/^pilots needed/i.test(label)) {
      cols.forEach(({ col, period }) => { const p = periods.find((x) => x.period === period); const n = Number(r[col]); if (p && Number.isFinite(n) && r[col] !== "") p.needed = n; });
      continue;
    }
    if (/^period selected/i.test(label) || /^check$/i.test(label)) continue;
    if (!label) continue;
    const on = cols.filter(({ col }) => /^x$/i.test(r[col] || "")).map(({ period }) => period);
    pilots.push({ label, periods: on });
  }
  if (!pilots.length) problems.push("No pilot rows found under the END row.");
  return { periods, pilots, problems };
}

// "Collins D." -> the one active pilot with that surname and initial.
// Returns { pilot } or { error } (never a guess: two matches is an error).
export function resolveWinterLabel(label, pilots) {
  const m = String(label || "").trim().match(/^([A-Za-z' -]+?)(?:\s+([A-Za-z])\.?)?$/);
  if (!m) return { error: `Could not read the name "${label}".` };
  const surname = m[1].trim().toUpperCase();
  const initial = (m[2] || "").toUpperCase();
  const active = (pilots || []).filter((p) => p.is_active !== false && (p.last_name || "").trim().toUpperCase() === surname);
  let cands = active;
  if (initial) cands = active.filter((p) => (p.first_name || "").trim().toUpperCase().startsWith(initial));
  if (cands.length === 1) return { pilot: cands[0] };
  if (!cands.length) return { error: `"${label}": no active pilot on the roster matches.` };
  return { error: `"${label}": ${cands.length} pilots share that name (${cands.map((p) => `${p.first_name} ${p.last_name}`).join(", ")}); add an initial.` };
}

// Roster rows -> the pilots on dispatch per period after trades are applied.
// { [period]: Set(pilot_id) }
export function onCallByPeriod(dutyRows) {
  const sheet = {}, tradeOn = {}, tradeOff = {};
  for (const r of dutyRows || []) {
    const bucket = r.source === "trade" ? (r.on_duty ? tradeOn : tradeOff) : (r.on_duty ? sheet : null);
    if (!bucket) continue;
    (bucket[r.period] = bucket[r.period] || new Set()).add(r.pilot_id);
  }
  const out = {};
  const periods = new Set([...Object.keys(sheet), ...Object.keys(tradeOn), ...Object.keys(tradeOff)].map(Number));
  for (const p of periods) {
    const s = new Set(sheet[p] || []);
    for (const id of tradeOff[p] || []) s.delete(id);
    for (const id of tradeOn[p] || []) s.add(id);
    out[p] = s;
  }
  return out;
}

export function periodForDate(periods, date) {
  return (periods || []).find((p) => p.start <= date && date <= p.end) || null;
}

// The periods one pilot is on dispatch for, as { period, start, end } in order.
export function onCallPeriodsFor(pilotId, dutyRows, periods) {
  const by = onCallByPeriod(dutyRows);
  return (periods || []).filter((p) => by[p.period]?.has(pilotId)).map((p) => ({ period: p.period, start: p.start, end: p.end }));
}

export function isOnCall(pilotId, date, dutyRows, periods) {
  const p = periodForDate(periods, date);
  if (!p) return false;
  return !!onCallByPeriod(dutyRows)[p.period]?.has(pilotId);
}

// Every ISO date a pilot is on dispatch (for the tax page's duty-day count).
export function onCallDates(pilotId, dutyRows, periods) {
  const out = [];
  for (const p of onCallPeriodsFor(pilotId, dutyRows, periods)) {
    for (let d = p.start; d <= p.end; d = addDays(d, 1)) out.push(d);
  }
  return out;
}

// Which season to show: the one still running (or next to run), else the latest.
export function currentSeason(periods, today) {
  const seasons = {};
  for (const p of periods || []) {
    const s = seasons[p.season] || (seasons[p.season] = { season: p.season, start: p.start, end: p.end });
    if (p.start < s.start) s.start = p.start;
    if (p.end > s.end) s.end = p.end;
  }
  const list = Object.values(seasons).sort((a, b) => a.start.localeCompare(b.start));
  return list.find((s) => s.end >= today)?.season || list[list.length - 1]?.season || null;
}

export function fmtPeriod(p) {
  const f = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
  return `${f(p.start)} to ${f(p.end)}`;
}
